import dayjs from 'dayjs';
import { Option } from '@g17eco/molecules/select/SelectFactory';
import { LOCAL_TIMEZONE } from './date';

const getUtcOffsetLabel = (tzName: string): string => {
  const offset = dayjs().tz(tzName).utcOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  if (minutes > 0) {
    return `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
  }
  return `GMT${sign}${hours}`;
};

const getLongTimezoneName = (tzName: string): string => {
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tzName, timeZoneName: 'long' });
  const parts = formatter.formatToParts(new Date());
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? tzName;
};

const buildTimezoneOption = (tzName: string): Option<string> => {
  const offsetLabel = getUtcOffsetLabel(tzName);
  const longName = getLongTimezoneName(tzName);
  const label = `(${offsetLabel}) ${longName} - ${tzName}`;
  const cityPart = tzName.split('/').pop()?.replace(/_/g, ' ') ?? '';

  return {
    value: tzName,
    label,
    searchString: `${tzName} ${longName} ${offsetLabel} ${cityPart}`,
  };
};

const buildTimezoneOptions = (): Option<string>[] => {
  const utcOption: Option<string> = {
    value: 'UTC',
    label: '(GMT+0) Coordinated Universal Time - UTC',
    searchString: 'UTC GMT+0 Coordinated Universal Time',
  };

  const timezones = Intl.supportedValuesOf('timeZone');
  const options = timezones.map(buildTimezoneOption);

  options.sort((a, b) => {
    const offsetA = dayjs().tz(a.value).utcOffset();
    const offsetB = dayjs().tz(b.value).utcOffset();
    if (offsetA !== offsetB) {
      return offsetA - offsetB;
    }
    return String(a.label).localeCompare(String(b.label));
  });

  return [utcOption, ...options];
};

export const TIMEZONE_OPTIONS = buildTimezoneOptions();

export const getDefaultTimezoneOption = (): Option<string> => {
  return TIMEZONE_OPTIONS.find((opt) => opt.value === LOCAL_TIMEZONE) ?? TIMEZONE_OPTIONS[0];
};
